/**
 * Intake Flow Page
 *
 * Container page for the intake wizard
 * Handles loading questions and existing request data
 */

import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import IntakeWizard from '../components/IntakeFlow/IntakeWizard';
import api from '../services/api';
import { ROUTES } from '../config/constants';
import { TYPE_CODES, GPR_SUBTYPE_ANSWER_TO_CODE } from '../config/requestTypeConstants';
import './IntakeFlow.css';

const IntakeFlow = () => {
  const { id: requestId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [requestData, setRequestData] = useState(null);
  const [resolvedTypeCode, setResolvedTypeCode] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Get request type and GPR subtype from location state (for new requests)
  const requestType = location.state?.requestType;
  const gprSubtype = location.state?.gprSubtype;
  const [showGprSelector, setShowGprSelector] = useState(false);

  useEffect(() => {
    api.getCurrentUser()
      .then(res => setCurrentUser(res.data.data.user || res.data.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        let formType = requestType || 'Capability Update Catalog'; // use request type or default
        let loadedRequest = null;

        // Load existing request if editing - do this FIRST to get the request_type
        if (requestId) {
          const requestResponse = await api.getRequestById(requestId);
          const request = requestResponse.data.data.request;
          loadedRequest = request;

          // Use the request's type_code to determine the form type
          formType = request.requestType?.type_code;

          // For legacy GPR requests, check the gpr_subtype in answers to resolve the actual subtype
          if (formType === TYPE_CODES.GPR && request.answers) {
            const gprSubtypeAnswer = request.answers.find(a => a.question_key === 'gpr_subtype');
            if (gprSubtypeAnswer) {
              formType = GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtypeAnswer.answer_value] || formType;
            }
          }
        } else {
          // For new requests, determine form type based on request type and GPR subtype
          // requestType is already a type_code value
          if (requestType === TYPE_CODES.GPR && gprSubtype) {
            formType = GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtype] || requestType;
          }
        }

        // Store the resolved type code so IntakeWizard gets the correct value
        setResolvedTypeCode(formType);

        // Load questions for the determined form type
        const questionsResponse = await api.getQuestions(formType);
        setQuestions(questionsResponse.data.data.questions);

        // Set request data if we loaded it
        if (loadedRequest) {
          // Transform answers array to object
          const answersObj = {};
          if (loadedRequest.answers) {
            loadedRequest.answers.forEach((answer) => {
              answersObj[answer.question_key] = answer.answer_value;
            });
          }

          // If there's a skiRelease relation, use it as fallback if no answer exists
          // This ensures we prefer the answer value over the relationship (for updated values)
          if (loadedRequest.skiRelease && !answersObj.ski_release) {
            answersObj.ski_release = loadedRequest.skiRelease.full_name;
          }

          // If there's a product relation, populate the correct product name answer key for the type
          const productName = loadedRequest.product?.product_name || loadedRequest.product_name;
          if (productName) {
            const typeCode = loadedRequest.requestType?.type_code || loadedRequest.type_code;
            const productKeyByType = {
              [TYPE_CODES.SOR]:             'salesops_product_capability_name',
              [TYPE_CODES.BREAKING_CHANGE]: 'bc_product_name',
              [TYPE_CODES.CIC]:             'bc_product_name',
              [TYPE_CODES.PPP]:             'ppp_product_name',
              [TYPE_CODES.PRIVACY]:         'privacy_product_name',
              [TYPE_CODES.GPR_GA]:          'gpr_ga_product_name',
              [TYPE_CODES.GPR_EX]:          'gpr_ex_product_name',
              [TYPE_CODES.GPR_INT]:         'gpr_int_product_name',
              [TYPE_CODES.GPR_MKT]:         'gpr_mkt_product_name',
              [TYPE_CODES.GPR_EA]:          'gpr_ea_product_name',
              [TYPE_CODES.GPR_RUNBOOK]:     'gpr_rb_product_name',
            };
            const productKey = productKeyByType[typeCode] || 'product_capability_name';
            answersObj[productKey] = productName;
          }

          // If there's a product relation with categorization, populate it if not already set
          if (loadedRequest.product?.categorization && !answersObj.categorization) {
            answersObj.categorization = loadedRequest.product.categorization;
          }

          setRequestData({
            ...loadedRequest,
            // Extract product_name from product relationship if available
            product_name: productName,
            answers: answersObj,
          });
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [requestId, requestType, gprSubtype]);

  if (loading) {
    return (
      <div className="intake-flow__loading">
        <div className="intake-flow__spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="intake-flow__error">
        <svg width="48" height="48" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <h2>Error Loading Intake Form</h2>
        <p>{error}</p>
        <div className="intake-flow__error-actions">
          <button onClick={() => window.location.reload()}>Retry</button>
          <button onClick={() => history.push(ROUTES.DASHBOARD)}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  // If new request but no request type specified, show type selector
  if (!requestId && !requestType && !showGprSelector) {
    const productLaunchTypes = [
      {
        label: 'GPR',
        value: TYPE_CODES.GPR,
        tooltip: "Submit a GPR request to validate a new or existing capability is safe, operable, observable, supportable, and commercially ready before it's exposed internally or externally",
        icon: (
          // Clipboard check — readiness checklist
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#4ade80">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ),
      },
      {
        label: 'Platform Packaging & Pricing',
        value: TYPE_CODES.PPP,
        tooltip: 'Submit this request for review and assessment, if capabilities, entitlements, overage pricing, and NPE options need to be updated for a capability',
        icon: (
          // Cube — product packaging units
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#c084fc">
            <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
          </svg>
        ),
      },
      {
        label: 'Pricing & Packaging',
        value: TYPE_CODES.PRICING_PACKAGING,
        tooltip: 'Submit this request to create pricing and packaging criteria for a capability using inputs from various teams (only used by the SalesForce team)',
        icon: (
          // Price tag — selling price of a product
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#fcd34d">
            <path fillRule="evenodd" d="M17.707 9.293a1 1 0 000-1.414l-7-7a1 1 0 00-.707-.293H5a2 2 0 00-2 2v5a2 2 0 00.586 1.414l7 7a1 1 0 001.414 0l6-6a1 1 0 000-1.414zM7 7a1 1 0 110-2 1 1 0 010 2z" clipRule="evenodd" />
          </svg>
        ),
      },
      {
        label: 'SalesOps',
        value: TYPE_CODES.SOR,
        tooltip: 'Submit this request for review and assessment, if a GA capability has pricing & packaging criteria',
        icon: (
          // Briefcase — how sales will sell the product
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#fb923c">
            <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
            <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
          </svg>
        ),
      },
      {
        label: 'Privacy',
        value: TYPE_CODES.PRIVACY,
        tooltip: 'Submit this request to assess how customer data/PII is collected, used, stored, logged, and shared for a capability',
        icon: (
          // Shield check — protecting PII and personal data
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#f87171">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ),
      },
    ];
    const productSupportTypes = [
      {
        label: 'Capability Update Catalog',
        value: TYPE_CODES.CUC,
        tooltip: 'Submit this request, if update adoption criteria (customer and Guidewire) needs to be maintained for a capability. This information will be used by TAMs so only key capabilities should be added.',
        icon: (
          // Stacked layers — update adoption catalog
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#2dd4bf">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
          </svg>
        ),
      },
      {
        label: 'Breaking Change',
        value: TYPE_CODES.BREAKING_CHANGE,
        tooltip: 'Submit this request for review and assessment, if a change to a capability will force a customer to modify code, configuration, or environments to consume the new version',
        icon: (
          // Lightning bolt — disruptive change requiring customer action
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#fb7185">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
        ),
      },
      {
        label: 'CiC',
        value: TYPE_CODES.CIC,
        tooltip: 'Submit this request for review and assessment, if a high-impact customer communication is required for a capability',
        icon: (
          // Speakerphone/megaphone — customer impact communications
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#7dd3fc">
            <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
          </svg>
        ),
      },
    ];

    const TooltipIcon = () => (
      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    );

    const renderCard = ({ label, value, icon, tooltip }) => (
      <button
        key={value}
        className="intake-flow__type-card"
        onClick={() => {
          if (value === 'GPR') {
            setShowGprSelector(true);
          } else {
            history.push(ROUTES.INTAKE_NEW, { requestType: value });
          }
        }}
      >
        {tooltip && (
          <span className="intake-flow__tooltip-anchor" onClick={e => e.stopPropagation()}>
            <TooltipIcon />
            <span className="intake-flow__tooltip-text">{tooltip}</span>
          </span>
        )}
        <div className="intake-flow__type-icon">
          {icon}
        </div>
        <h3>{label}</h3>
      </button>
    );

    const productDefinitionTypes = [
      {
        label: 'New Product Request',
        value: TYPE_CODES.NEW_PRODUCT_REQUEST,
        tooltip: 'Request a new product or capability be created by the SalesForce team. For new capabilities this will always be the first step in the product launch process.',
        icon: (
          // Sparkle / star — brand new product creation
          <svg width="28" height="28" viewBox="0 0 20 20" fill="#818cf8">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ),
      },
    ];

    return (
      <div className="intake-flow__type-selector">
        <h1>New Request</h1>
        <p>Select the request type you want to create.</p>

        <div className="intake-flow__type-section">
          <div className="intake-flow__type-section-header">
            <h2>Product Definition</h2>
          </div>
          <div className="intake-flow__type-cards">
            {productDefinitionTypes.map(renderCard)}
          </div>
        </div>

        <div className="intake-flow__type-section">
          <div className="intake-flow__type-section-header">
            <h2>Product Launch</h2>
          </div>
          <div className="intake-flow__type-cards">
            {productLaunchTypes.map(renderCard)}
          </div>
        </div>

        <div className="intake-flow__type-section intake-flow__type-section--support">
          <div className="intake-flow__type-section-header">
            <h2>Product Support</h2>
          </div>
          <div className="intake-flow__type-cards intake-flow__type-cards--support">
            {productSupportTypes.map(renderCard)}
          </div>
        </div>
      </div>
    );
  }

  // If GPR was selected, show GPR subtype selector
  if (!requestId && showGprSelector && !gprSubtype) {
    const gprSubtypes = [
      {
        key: 'New GA Service',
        title: 'New GA Service',
        description: 'Launch a new Generally Available service',
        tooltip: 'Submit this request for a capability, which will run in customer production and must meet full production-readiness gates (architecture, observability, DR, commercial readiness, etc.). Use this form for RA',
        icon: '🚀',
      },
      {
        key: 'Runbook Changes',
        title: 'Runbook Changes',
        description: 'Update operational runbooks and procedures',
        tooltip: 'Submit this request for an existing service – runbook update only, where the change is primarily to operational/runbook documentation and procedures, not to the underlying service behavior',
        icon: '📋',
      },
      {
        key: 'EA Service',
        title: 'New EA Service',
        description: 'Launch a new Early Access service',
        tooltip: 'Submit this request for a new Early Access (EA) capability, focused on readiness for use in customer non-production environments ahead of a later GA GPR',
        icon: '🔬',
      },
      {
        key: 'Existing Service',
        title: 'Existing Service',
        description: 'Change to an existing GA capability',
        tooltip: 'Submit this request for a material change to an existing GA service/capability that impacts how it operates, is maintained, or affects cost/contract/customer behavior',
        icon: '🔄',
      },
      {
        key: 'Internal Service',
        title: 'New Internal Service',
        description: 'A new internal capability',
        tooltip: 'Submit this request for a new internal-only platform service (not customer-priced) to ensure it is operable, observable, and supportable in GWCP',
        icon: '🏢',
      },
      {
        key: 'Marketplace',
        title: 'Marketplace',
        description: 'A new Marketplace request',
        tooltip: 'Submit this request for a new Marketplace capability that will be listed/sold via Marketplace (typically with its own SKU/pricing) and must satisfy GA production-readiness',
        icon: '🛒',
      },
    ];

    const GprTooltipIcon = () => (
      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    );

    return (
      <div className="intake-flow__type-selector">
        <button
          className="intake-flow__back-button"
          onClick={() => setShowGprSelector(false)}
        >
          ← Back
        </button>
        <h1>Select Production Readiness Intake Type</h1>
        <p>Choose the specific type of production readiness intake:</p>
        <div className="intake-flow__type-cards">
          {gprSubtypes.map((subtype) => (
            <button
              key={subtype.key}
              className="intake-flow__type-card"
              onClick={() =>
                history.push(ROUTES.INTAKE_NEW, {
                  requestType: 'GPR',
                  gprSubtype: subtype.key
                })
              }
            >
              {subtype.tooltip && (
                <span className="intake-flow__tooltip-anchor" onClick={e => e.stopPropagation()}>
                  <GprTooltipIcon />
                  <span className="intake-flow__tooltip-text">{subtype.tooltip}</span>
                </span>
              )}
              <div className="intake-flow__type-icon">
                <span className="intake-flow__type-emoji">{subtype.icon}</span>
              </div>
              <h3>{subtype.title}</h3>
              <p className="intake-flow__type-description">{subtype.description}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Check if this is an under-construction request type with no questions
  const isUnderConstruction =
    !requestId && // Only for new requests, not edits
    requestType === 'Revenue' &&
    questions.length === 0;

  if (isUnderConstruction) {
    return (
      <div className="intake-flow">
        <div className="intake-flow__under-construction">
          <div className="intake-flow__construction-icon">
            🚧
          </div>
          <h2 className="intake-flow__construction-title">
            {requestType} is Under Construction
          </h2>
          <p className="intake-flow__construction-message">
            This request type is currently being configured. Please check back later or contact your administrator.
          </p>
          <button
            className="intake-flow__back-to-dashboard"
            onClick={() => history.push(ROUTES.DASHBOARD)}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Use the resolved type code from state (set during data loading) as the effective type.
  // This ensures edit mode correctly gets the resolved sub-type (e.g. GPR_EA not GPR).
  const effectiveRequestType = resolvedTypeCode || requestType;

  return (
    <div className="intake-flow">
      <IntakeWizard
        requestId={requestId}
        requestType={effectiveRequestType}
        gprSubtype={gprSubtype}
        initialData={requestData}
        questions={questions}
        currentUser={currentUser}
      />
    </div>
  );
};

export default IntakeFlow;
